import { useRef, useState } from 'react';
import type { useCheckMonitor } from '../../hooks/useCheckMonitor';
import type { CheckMonitorPeriod } from 'types/checkMonitor';
import { displayInstant } from './calendar';
import { labelledDevices } from './series';
import { SeriesMarker } from './SeriesMarker';
import { CheckMonitorChart } from './CheckMonitorChart';
import styles from './CheckMonitor.module.scss';

export const CheckMonitor = ({
  monitor,
}: {
  monitor: ReturnType<typeof useCheckMonitor>;
}) => {
  const {
    query,
    data,
    loading,
    error,
    following,
    setPeriod,
    navigate,
    refresh,
  } = monitor;
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const showAll = useRef<HTMLButtonElement>(null);
  const periodControl = useRef<HTMLButtonElement>(null);
  const devices = data ? labelledDevices(data) : [];
  const visible = devices.filter((device) => !hidden.has(device.serialNumber));
  const hasCoverage = data?.buckets.some((bucket) => bucket.available);
  const beforeCoverage = data?.buckets.some(
    (bucket) => bucket.unavailableReason === 'before_coverage'
  );
  const total =
    data?.series.reduce(
      (sum, series) =>
        sum +
        series.counts.reduce<number>(
          (subtotal, count) => subtotal + (count ?? 0),
          0
        ),
      0
    ) ?? 0;
  const allHidden = devices.length > 0 && visible.length === 0;
  const restore = () => setHidden(new Set());
  const toggleSeries = (serial: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });

  return (
    <section
      className={styles.monitor}
      aria-labelledby="check-monitor-title"
      data-cy="check-monitor"
    >
      <div className={styles.heading}>
        <div>
          <h2 id="check-monitor-title">Checadas por dispositivo</h2>
          <p>Checadas registradas en ProSinergia.</p>
        </div>
        {data && (
          <div className={styles.summary}>
            <strong>{hasCoverage ? total.toLocaleString('es-MX') : '—'}</strong>
            <span>
              {hasCoverage ? 'registradas' : 'sin datos disponibles'} ·{' '}
              {devices.length} dispositivos activos
            </span>
          </div>
        )}
      </div>
      <div className={styles.toolbar}>
        <div className={styles.periods} role="group" aria-label="Temporalidad">
          {(
            [
              ['day', 'Día'],
              ['week', 'Semana'],
              ['month', 'Mes'],
            ] as [CheckMonitorPeriod, string][]
          ).map(([period, label]) => (
            <button
              type="button"
              key={period}
              ref={period === query.period ? periodControl : undefined}
              aria-pressed={period === query.period}
              onClick={() => setPeriod(period)}
              data-cy={`monitor-${period}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={styles.navigation}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={!query.anchor}
            aria-label="Periodo anterior"
            data-cy="monitor-previous"
          >
            ← Anterior
          </button>
          <button
            type="button"
            onClick={() => navigate(1)}
            disabled={following || !query.anchor}
            aria-label="Periodo siguiente"
            data-cy="monitor-next"
          >
            Siguiente →
          </button>
        </div>
      </div>
      <div className={styles.metadata}>
        <p data-cy="monitor-range">
          {data
            ? `${displayInstant(data.start, data.timeZone)} – ${displayInstant(new Date(+new Date(data.end) - 1).toISOString(), data.timeZone)}`
            : 'Consultando periodo…'}
        </p>
        <span data-cy="monitor-zone">
          {data?.timeZone || query.timeZone || 'Zona horaria del servidor'}
        </span>
      </div>
      {beforeCoverage && (
        <p className={styles.notice} data-cy="monitor-coverage">
          {hasCoverage
            ? 'Cobertura parcial del periodo.'
            : 'Sin cobertura para este periodo.'}{' '}
          Los datos están disponibles desde{' '}
          {displayInstant(data!.coverageStart, data!.timeZone, true)}.
        </p>
      )}
      {error && (
        <div role="alert" className={styles.error} data-cy="monitor-error">
          <strong>
            {data
              ? 'Datos potencialmente desactualizados.'
              : 'No fue posible cargar el periodo.'}
          </strong>{' '}
          {error}{' '}
          <button type="button" onClick={refresh} disabled={loading}>
            Reintentar
          </button>
        </div>
      )}
      {!data && loading && (
        <p role="status" className={styles.empty} data-cy="monitor-loading">
          Cargando checadas registradas…
        </p>
      )}
      {data && (
        <>
          {devices.length === 0 ? (
            <p className={styles.empty} data-cy="monitor-empty">
              Sin dispositivos activos
            </p>
          ) : (
            <>
              <div
                className={styles.legend}
                role="group"
                aria-label="Dispositivos visibles"
              >
                {devices.map((device) => (
                  <button
                    type="button"
                    key={device.serialNumber}
                    aria-pressed={!hidden.has(device.serialNumber)}
                    data-cy="monitor-legend"
                    data-serial={device.serialNumber}
                    onClick={() => toggleSeries(device.serialNumber)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (!event.repeat) toggleSeries(device.serialNumber);
                      }
                    }}
                  >
                    <svg
                      width="36"
                      height="18"
                      aria-hidden="true"
                      fill={device.color}
                      stroke={device.color}
                    >
                      <path d="M0,9 H36" strokeDasharray={device.dash} />
                      <SeriesMarker marker={device.marker} x={18} y={9} />
                    </svg>
                    <span>{device.label}</span>
                    <span className={styles.visibility}>
                      {hidden.has(device.serialNumber) ? 'Oculta' : 'Visible'}
                    </span>
                  </button>
                ))}
              </div>
              {allHidden ? (
                <p className={styles.empty} data-cy="monitor-hidden">
                  Todas las series están ocultas.{' '}
                  <button type="button" ref={showAll} onClick={restore}>
                    Mostrar todas
                  </button>
                </p>
              ) : hasCoverage && total === 0 ? (
                <p className={styles.empty} data-cy="monitor-zero">
                  Sin checadas registradas en este periodo.
                </p>
              ) : null}
            </>
          )}
          <CheckMonitorChart
            data={data}
            devices={visible}
            fallbackFocus={() =>
              (showAll.current || periodControl.current)?.focus()
            }
          />
          <footer className={styles.footer}>
            <span data-cy="monitor-updated">
              Última consulta exitosa:{' '}
              {displayInstant(data.generatedAt, data.timeZone, true)}
            </span>
            <span role="status">
              {loading
                ? 'Actualizando…'
                : following
                  ? 'Actualización cada 60 segundos con la pestaña visible'
                  : 'Periodo histórico · actualización automática pausada'}
            </span>
          </footer>
        </>
      )}
    </section>
  );
};
